import { z } from 'zod';

import {
  validateEmploymentTypeRefine,
  validateEmploymentTypesRefine,
} from '@/components/form/fieldDefs/employmentTypeFields';
import { buildFormSchema } from '@/utils/validation/formSchemaKit';
import {
  buildCommonValidationMessages,
  emailField,
  optionalString,
  phoneField,
  requiredBoolean,
  requiredPresentField,
  requiredString,
  withMaxLength,
} from '@/utils/validation/schemaHelpers';

import type { EmploymentType } from '@/types/employmentType';
import type { FormSchema } from '@/utils/validation/formSchemaKit';
import type { TFunction } from 'i18next';

export const buildCandidateCreateFormSchema = (
  t: TFunction,
  employmentTypes: EmploymentType[] = [],
): FormSchema => {
  const messages = buildCommonValidationMessages(t);
  return buildFormSchema(
    {
      email: emailField(messages),
      first_name: withMaxLength(
        requiredString(messages.required),
        64,
        messages.maxLength(64),
      ),
      last_name: withMaxLength(
        requiredString(messages.required),
        64,
        messages.maxLength(64),
      ),
      phone: phoneField(messages, false),
      company: optionalString(),
      compensation_currency: optionalString(),
      employment_types: {
        schema: z.array(z.record(z.string(), z.unknown())).min(1),
        isRequired: true,
      },
    },
    (schema) =>
      schema.superRefine((data, ctx) => {
        validateEmploymentTypesRefine(
          data,
          ctx,
          employmentTypes,
          'candidate',
          messages.required,
        );
      }),
  );
};

export const buildCandidateRejectFormSchema = (t: TFunction): FormSchema => {
  const messages = buildCommonValidationMessages(t);
  return buildFormSchema({
    rejection_reason: requiredString(messages.required),
  });
};

export const buildCandidateOfferFormSchema = (
  t: TFunction,
  employmentTypes: EmploymentType[] = [],
): FormSchema => {
  const messages = buildCommonValidationMessages(t);
  return buildFormSchema(
    {
      offer_doc: requiredPresentField(messages.required),
      start_date: requiredPresentField(messages.required),
      role: withMaxLength(
        requiredString(messages.required),
        32,
        messages.maxLength(32),
      ),
      other_benefits: withMaxLength(
        optionalString(),
        255,
        messages.maxLength(255),
      ),
      comment: optionalString(),
    },
    (schema) =>
      schema.superRefine((data, ctx) => {
        validateEmploymentTypeRefine(
          data,
          ctx,
          employmentTypes,
          'offer',
          messages.required,
          ['compensation'],
        );
      }),
  );
};

export const buildCandidateIntroduceFormSchema = (t: TFunction): FormSchema => {
  const messages = buildCommonValidationMessages(t);
  return buildFormSchema(
    {
      agree: requiredBoolean(messages.required),
      comments: optionalString(),
    },
    (schema) =>
      schema.superRefine((data, ctx) => {
        if (!data.agree) {
          ctx.addIssue({
            code: 'custom',
            path: ['agree'],
            message: messages.required,
          });
        }
      }),
  );
};

export const buildFeedbackFormSchema = (t: TFunction): FormSchema => {
  const messages = buildCommonValidationMessages(t);
  return buildFormSchema({
    comments: requiredString(messages.required),
    decision: requiredString(messages.required),
    agree: requiredBoolean(messages.required),
  });
};

export const buildConfirmModalFormSchema = (t: TFunction): FormSchema => {
  const messages = buildCommonValidationMessages(t);
  return buildFormSchema({
    rejection_reason: requiredString(messages.required),
  });
};

export const buildRecruiterOfferCommentFormSchema = (
  t: TFunction,
): FormSchema => {
  const messages = buildCommonValidationMessages(t);
  return buildFormSchema({
    comment: requiredString(messages.required),
  });
};

export const buildCandidateStatusFormSchema = (t: TFunction): FormSchema => {
  const messages = buildCommonValidationMessages(t);
  return buildFormSchema({
    status: requiredString(messages.required),
  });
};
